import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';

import { ServiceError, useError } from 'Src/network/useError';

import service, { TerminalAndLocation } from './provisionService';

const queryKeys = {
  getTerminal: (...args: Parameters<typeof service.terminal.getTerminal>) => ['PROVISIONER:GET:Terminal', ...args],
};

const useQuery_GetTerminal = (...args: Parameters<typeof service.terminal.getTerminal>) => {
  const { t } = useTranslation();
  const { parseError } = useError({ activity: t('provision.labels.loadTerminal') });
  return useQuery<TerminalAndLocation, ServiceError>({
    queryKey: queryKeys.getTerminal(...args),
    queryFn: async () => {
      try {
        return await service.terminal.getTerminal(...args);
      } catch (e) {
        throw parseError(e);
      }
    },
    // only make requests when required arguments are fulfilled
    enabled: args.length > 0 && Boolean(args[0].code),
  });
};

const useMutation_ProvisionTerminal = (onError?: (error: ServiceError) => void) => {
  const { t } = useTranslation();
  const { parseError } = useError({ activity: t('provision.errors.provisionTerminal') });
  return useMutation({
    mutationFn: async (...args: Parameters<typeof service.terminal.provisionTerminal>) => {
      try {
        return await service.terminal.provisionTerminal(...args);
      } catch (e) {
        throw parseError(e);
      }
    },
    onError,
  });
};

const useMutation_KillProvisionClaim = (onError?: (error: ServiceError) => void) => {
  const { t } = useTranslation();
  const { parseError } = useError({ activity: t('provision.errors.killTerminal') });
  return useMutation({
    mutationFn: async (...args: Parameters<typeof service.terminal.killProvisionClaim>) => {
      try {
        return await service.terminal.killProvisionClaim(...args);
      } catch (e) {
        throw parseError(e);
      }
    },
    onError,
  });
};

const useProvisionerTerminal = {
  query: {
    getTerminal: useQuery_GetTerminal,
  },
  mutate: {
    provisionTerminal: useMutation_ProvisionTerminal,
    killProvisionClaim: useMutation_KillProvisionClaim,
  }
};

export { useProvisionerTerminal };

